<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
// NOTE: Required both imports to make useKeenSlider library work
// Check https://keen-slider.io/docs for official documentation
import { useKeenSlider } from 'keen-slider/vue';

import AppButton from '../../appButton/AppButton.vue';
import AppNumberOfSlides from '../AppNumberOfSlides.vue';
import AppGalleryDots from '../AppGalleryDots.vue';
import AppImg from '../../appImg/AppImg.vue';
import AppLazyLoadComponent from '../../appLazyLoadComponent/AppLazyLoadComponent.vue';

export interface AppSwiperGalleryProps {
  imageUrls: string[];
  displayGalleryDots?: boolean;
  allowFullGallery?: boolean;
  showNavButtons?: boolean;
  showNumberOfSlides?: boolean;
  printMode?: boolean;
  slidesAmountPosition?: 'left' | 'right';
  slidesAmountSize?: 's' | 'm';
  errorImageHandler?: (src: string) => void;
}

const props = withDefaults(defineProps<AppSwiperGalleryProps>(), {
  displayGalleryDots: true,
  allowFullGallery: true,
  showNavButtons: false,
  showNumberOfSlides: true,
  slidesAmountPosition: 'left',
});

interface AppSwiperGalleryEmits {
  (eventName: 'show-full-gallery'): void;
  (eventName: 'show-single-image', imageIndex: number): void;
  (eventName: 'gallery-image-swipe'): void;
  (eventName: 'mouse-enter'): void;
  (eventName: 'mouse-leave'): void;
}

const emit = defineEmits<AppSwiperGalleryEmits>();

const INITIAL_SLIDE = 0;

const currentSlide = ref<number>(INITIAL_SLIDE);
const isSliderLoaded = ref<boolean>(false);

const numberOfSlides = computed<number>(() => props.imageUrls.length);

// NOTE: Required core funtion to make useKeenSlider library work
const [container, slider] = useKeenSlider({
  initial: INITIAL_SLIDE,
  drag: !props.printMode,
  slideChanged(slider) {
    setCurrentSlide(slider.track.details.rel);
  },
  dragStarted(slider) {
    slider.update({
      ...slider.options,
    });
  },
  created() {
    isSliderLoaded.value = true;
  },
});

function setCurrentSlide(slide: number): void {
  currentSlide.value = slide;
}

function showNextImage(event: MouseEvent, slider): void {
  // INFO: fix of wrong calculated jump values to next/prev image
  const opt = slider.options;
  const newIndex = opt.initial < numberOfSlides.value - 1 ? opt.initial + 1 : 0;
  slider.update({
    ...opt,
    initial: newIndex,
  });

  slider.next();
  event.preventDefault();
  event.stopPropagation();
  emit('gallery-image-swipe');
}

function showPrevImage(event: MouseEvent, slider): void {
  // INFO: fix of wrong calculated jump values to next/prev image
  const opt = slider.options;
  const newIndex = opt.initial > 0 ? opt.initial - 1 : 0;
  slider.update({
    ...opt,
    initial: newIndex,
  });
  slider.prev();
  event.preventDefault();
  event.stopPropagation();
  emit('gallery-image-swipe');
}

// After imageUrls change, wait for the DOM update and then re-initialize the slider.
// This fixes the issue where the slider doesn't show the correct number of images after sorting.
watch(
  () => props.imageUrls,
  async () => {
    await nextTick();

    if (slider.value) {
      slider.value.update();
      slider.value.moveToIdx(0, false, { duration: 0 });
    }
    currentSlide.value = 0;
  }
);
</script>

<template>
  <div
    role="presentation"
    :class="['app-swiper-gallery', { 'print-mode': props.printMode }]"
    tabindex="0"
    @mouseenter="emit('mouse-enter')"
    @mouseleave="emit('mouse-leave')"
    @focusin="emit('mouse-enter')"
    @focusout="emit('mouse-leave')"
  >
    <!-- NOTE: Required classes keen-slider and keen-slider__slide to make useKeenSlider library work -->
    <div
      ref="container"
      class="keen-slider"
      role="presentation"
      @click="emit('show-single-image', currentSlide)"
    >
      <div
        v-for="(image, index) in props.imageUrls"
        :key="index"
        class="keen-slider__slide"
      >
        <AppLazyLoadComponent>
          <AppImg
            class="image-container"
            :src="image"
            alt="image"
            @img-load-error="errorImageHandler"
          />
        </AppLazyLoadComponent>
      </div>
    </div>
    <AppNumberOfSlides
      v-if="props.showNumberOfSlides"
      background="dark-background"
      class="number-of-slides"
      :size="props.slidesAmountSize"
      :current-slide="currentSlide"
      :number-of-slides="numberOfSlides"
      :class="[props.slidesAmountPosition, props.slidesAmountSize]"
    />
    <AppGalleryDots
      v-if="
        props.displayGalleryDots &&
        isSliderLoaded &&
        slider &&
        imageUrls.length > 1
      "
      class="gallery-dots"
      :current-slide="currentSlide"
      :number-of-slides="numberOfSlides"
      @dot-clicked="slider.moveToIdx($event)"
    />
    <AppButton
      v-if="props.allowFullGallery"
      class="gallery-button"
      type="secondary"
      state="enabled"
      size="medium"
      :icon="{ name: 'gallery' }"
      @click="emit('show-full-gallery')"
    />
    <div v-if="showNavButtons" class="nav-buttons">
      <AppButton
        v-if="isSliderLoaded && slider && imageUrls.length > 1"
        class="prev-image-button"
        type="secondary"
        state="enabled"
        size="small"
        :icon="{ name: 'chevronLeft' }"
        @click="showPrevImage($event, slider)"
      />

      <AppButton
        v-if="isSliderLoaded && slider && imageUrls.length > 1"
        class="next-image-button"
        type="secondary"
        state="enabled"
        size="small"
        :icon="{ name: 'chevronRight' }"
        @click="showNextImage($event, slider)"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
@import url('keen-slider/keen-slider.css');
@import '../../../styles/_media';

.app-swiper-gallery {
  position: relative;
  height: 100%;

  &.print-mode {
    cursor: default;
  }

  .keen-slider {
    height: 100%;
    padding: 0;

    .keen-slider__slide {
      height: 100%;
      width: 100%;
      max-width: 100% !important;
      min-width: 100% !important;
    }
  }
  .image-container {
    height: 100%;
    width: 100%;
    max-height: 425px;
    aspect-ratio: 4 / 3;
    transition: opacity 0.3s ease-in-out;
    cursor: pointer;
    @include on-hover {
      zoom: 1;
      filter: alpha(opacity=80);
      opacity: 0.8;
    }
  }

  .number-of-slides {
    position: absolute;
    bottom: var(--spacing-16);
    &.left {
      left: var(--spacing-16);
    }
    &.right {
      right: var(--spacing-16);
    }
    &.s {
      bottom: var(--spacing-8);
      &.left {
        left: var(--spacing-8);
      }
      &.right {
        right: var(--spacing-8);
      }
    }
  }

  .gallery-dots {
    position: absolute;
    bottom: var(--spacing-16);
    left: 0;
    right: 0;
    top: auto;
  }

  .gallery-button {
    position: absolute;
    bottom: var(--spacing-16);
    right: var(--spacing-16);
  }

  .nav-buttons {
    .prev-image-button,
    .next-image-button {
      display: none;
    }
  }
  @include on-hover {
    .nav-buttons {
      .prev-image-button {
        position: absolute;
        top: 50%;
        left: var(--spacing-16);
        transform: translateY(-50%);
        display: flex;
      }

      .next-image-button {
        display: flex;
        position: absolute;
        top: 50%;
        right: var(--spacing-16);
        transform: translateY(-50%);
      }
    }
  }
}
</style>
